import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { LoadingDots } from "@/components/ui/loading-dots";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Transitions scoped to the properties that actually change (couleurs +
  // ombre) — `transition-all` est evité pour ne pas animer transform/padding/...
  // sur chaque clic et amplifier la perception de latence. L'`active:scale` est
  // appliqué de manière instantanée pour un feedback "snappy" sans attente.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg ring-offset-background transition-[background-color,color,border-color,box-shadow] duration-fast active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 relative",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-soft [&_*]:!text-primary-foreground",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-soft",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    // La typographie suit les couleurs de variante : dans un cn(), une couleur
    // écrite après text-payfit-body l'efface (cf. src/lib/utils.ts), et les
    // variantes default, destructive, secondary et link tombaient en 16 px / 400.
    // Une variante composée sans condition s'applique toujours, après `variant`
    // et `size`, avant le className de l'appelant.
    compoundVariants: [{ class: "text-payfit-body" }],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  /** Affiche un checkmark animé en lieu et place du contenu. Le parent reste
   *  responsable de la durée d'affichage (typiquement ~600ms avant fermeture). */
  success?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, success = false, disabled, children, ...props }, ref) => {
    const mergedDisabled = disabled || loading || success;
    const hideContentClassName = loading || success ? "text-transparent hover:text-transparent pointer-events-none relative" : undefined;

    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }), hideContentClassName)}
          ref={ref as React.ComponentPropsWithRef<typeof Slot>["ref"]}
          aria-disabled={mergedDisabled}
          aria-busy={loading}
          data-disabled={mergedDisabled ? "" : undefined}
          {...(props as React.ComponentPropsWithoutRef<typeof Slot>)}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }), hideContentClassName)}
        ref={ref}
        disabled={mergedDisabled}
        aria-disabled={mergedDisabled}
        aria-busy={loading}
        {...props}
      >
        {children}
        {/* Les overlays sont rendus APRÈS `children` : l'ordre DOM n'a aucun
            effet visuel (ils sont en `absolute inset-0`) mais il change la
            façon dont React les monte — `appendChild` en fin de parent au lieu
            d'un `insertBefore` placé devant le nœud de texte du label. Google
            Traduction remplace ce nœud de texte par ses propres balises <font>,
            ce qui faisait échouer l'`insertBefore` au passage en `loading`
            (NotFoundError, cf. Sentry JAVASCRIPT-REACT-Q). */}
        {loading && !success && (
          <div className="absolute inset-0 flex items-center justify-center">
            <LoadingDots className="text-current" colorClassName="bg-current" size={5} />
          </div>
        )}
        {loading && !success && (
          <span
            className="absolute inset-x-2 bottom-1 h-0.5 overflow-hidden rounded-full bg-current/10"
            aria-hidden="true"
          >
            <span className="absolute inset-y-0 left-0 w-[35%] bg-gradient-to-r from-transparent via-current to-transparent motion-progress-indeterminate" />
          </span>
        )}
        {success && (
          <span
            className="absolute inset-0 flex items-center justify-center"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12.5 L10 17.5 L19 7.5" pathLength={1} className="motion-check-draw" />
            </svg>
          </span>
        )}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
